import * as React from 'react';
import { Link, useParams } from 'react-router';
import {
  ArrowLeftIcon,
  AlertTriangleIcon,
  MapPinIcon,
  UserIcon,
  MailIcon,
  PhoneIcon,
  ShieldIcon,
  EyeIcon,
  MessageSquareIcon,
  GitMergeIcon,
  BellIcon,
  ExternalLinkIcon,
  BotIcon,
  SendIcon,
  LockIcon
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetWorkspaceReportDetail,
  useGetWorkspaceStaff,
  getGetWorkspaceReportsQueryKey,
  getGetWorkspaceKpiQueryKey,
  useAssignWorkspaceReport,
  useTransitionWorkspaceReport,
  useAddWorkspaceComment,
  useMergeWorkspaceReport
} from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { StatusTimeline } from '@/components/report/status-timeline';
import {
  getStatusConfig,
  formatDateTime,
  SEVERITY_LABELS,
  SEVERITY_COLORS
} from '@/lib/report-helpers';

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  submitted: ['acknowledged', 'rejected', 'duplicate'],
  acknowledged: ['in_progress', 'scheduled', 'resolved', 'rejected', 'duplicate'],
  in_progress: ['scheduled', 'resolved', 'closed', 'duplicate'],
  scheduled: ['in_progress', 'resolved', 'closed', 'duplicate'],
  resolved: ['closed'],
  closed: [],
  rejected: [],
  duplicate: []
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'duplicate', label: 'Duplicate' }
];

export function ReportDetailPage() {
  const { reportId = '' } = useParams<{ reportId: string }>();
  const qc = useQueryClient();

  const { data: report, isLoading } = useGetWorkspaceReportDetail(reportId);
  const { data: staffData } = useGetWorkspaceStaff();

  const [transitionOpen, setTransitionOpen] = React.useState(false);
  const [newStatus, setNewStatus] = React.useState('');
  const [transitionNote, setTransitionNote] = React.useState('');
  const [canonicalReportId, setCanonicalReportId] = React.useState('');

  const [commentBody, setCommentBody] = React.useState('');
  const [commentInternal, setCommentInternal] = React.useState(true);

  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [mergeCanonicalId, setMergeCanonicalId] = React.useState('');

  const staffList = staffData?.staff ?? [];
  const allowed = ALLOWED_TRANSITIONS[report?.status ?? 'submitted'] ?? [];

  const assignMutation = useAssignWorkspaceReport();
  const transitionMutation = useTransitionWorkspaceReport();
  const commentMutation = useAddWorkspaceComment();
  const mergeMutation = useMergeWorkspaceReport();

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: getGetWorkspaceReportsQueryKey() });
    qc.invalidateQueries({ queryKey: getGetWorkspaceKpiQueryKey() });
    qc.invalidateQueries({ queryKey: ['getWorkspaceReportDetail'] });
  }

  function handleAssign(staffId: string) {
    assignMutation.mutate(
      { reportId, data: { assignedToStaffId: staffId } },
      { onSuccess: invalidateAll }
    );
  }

  function handleTransition() {
    if (!newStatus || !transitionNote.trim()) return;
    transitionMutation.mutate(
      {
        reportId,
        data: {
          newStatus: newStatus as 'acknowledged' | 'in_progress' | 'scheduled' | 'resolved' | 'closed' | 'rejected' | 'duplicate',
          note: transitionNote,
          ...(newStatus === 'duplicate' && canonicalReportId ? { canonicalReportId } : {})
        }
      },
      {
        onSuccess: () => {
          setTransitionOpen(false);
          setNewStatus('');
          setTransitionNote('');
          setCanonicalReportId('');
          invalidateAll();
        }
      }
    );
  }

  function handleAddComment() {
    if (!commentBody.trim()) return;
    commentMutation.mutate(
      { reportId, data: { body: commentBody, isInternal: commentInternal } },
      {
        onSuccess: () => {
          setCommentBody('');
          invalidateAll();
        }
      }
    );
  }

  function handleMerge() {
    if (!mergeCanonicalId.trim()) return;
    mergeMutation.mutate(
      { reportId, data: { canonicalReportId: mergeCanonicalId } },
      {
        onSuccess: () => {
          setMergeOpen(false);
          setMergeCanonicalId('');
          invalidateAll();
        }
      }
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Report not found.</p>
        <Button asChild variant="link" className="mt-2">
          <Link to="/workspace">Back to workspace</Link>
        </Button>
      </div>
    );
  }

  const statusCfg = getStatusConfig(report.status ?? 'submitted');
  const internalComments = report.comments?.filter((c) => c.isInternal) ?? [];
  const publicComments = report.comments?.filter((c) => !c.isInternal) ?? [];

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/workspace">
            <ArrowLeftIcon className="size-4" />
            Back
          </Link>
        </Button>
      </div>

      <PageHeader
        title={report.customTypeLabel ? `Other — ${report.customTypeLabel}` : (report.categoryName ?? 'Report')}
        icon={AlertTriangleIcon}
        description={`Created ${formatDateTime(report.createdAt)} · ${report.upvoteCount ?? 0} upvotes`}
      />

      {/* Status + actions bar */}
      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${statusCfg.badgeClass}`}>
          <span className={`size-2 rounded-full ${statusCfg.dotClass}`} />
          {statusCfg.label}
        </span>
        <Badge variant="outline" className={SEVERITY_COLORS[report.severity as keyof typeof SEVERITY_COLORS] ?? ''}>
          {SEVERITY_LABELS[report.severity as keyof typeof SEVERITY_LABELS] ?? report.severity}
        </Badge>
        {report.trackingToken && (
          <Button asChild variant="ghost" size="sm">
            <Link to={`/track/${report.trackingToken}`} target="_blank">
              <ExternalLinkIcon className="size-4" />
              Public tracking page
            </Link>
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          {allowed.length > 0 && (
            <Button size="sm" onClick={() => setTransitionOpen(true)}>
              Change Status
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setMergeOpen(true)}>
            <GitMergeIcon className="size-4" />
            Merge Duplicate
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column: photo + details */}
        <div className="space-y-4 lg:col-span-2">
          {/* Photo */}
          {report.photoUrl && (
            <Card>
              <CardContent className="p-0">
                <img
                  src={report.photoUrl}
                  alt="Report photo"
                  className="w-full rounded-xl object-cover"
                  style={{ maxHeight: '400px' }}
                />
              </CardContent>
            </Card>
          )}

          {/* Report info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Report Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.description && (
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="mt-1 text-sm">{report.description}</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <MapPinIcon className="size-4 text-muted-foreground" />
                <span>
                  {report.addressText ?? `${report.latitude?.toFixed(6)}, ${report.longitude?.toFixed(6)}`}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* AI detection */}
          {report.aiDetection && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BotIcon className="size-4 text-primary" />
                  AI Detection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="overflow-auto rounded-lg bg-muted p-3 text-xs">
                  {JSON.stringify(report.aiDetection, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Matched asset */}
          {report.matchedAsset && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Matched Asset</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">{report.matchedAsset.typeName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Owner</span>
                  <Badge variant="secondary">
                    {report.matchedAsset.ownerTenantName}
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({report.matchedAsset.ownerTenantType})
                    </span>
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Condition</span>
                  <span className="capitalize">{report.matchedAsset.condition}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status timeline */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Status Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <StatusTimeline entries={report.timeline ?? []} />
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquareIcon className="size-4" />
                Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs defaultValue="internal">
                <TabsList>
                  <TabsTrigger value="internal">
                    Internal ({internalComments.length})
                  </TabsTrigger>
                  <TabsTrigger value="public">
                    Public ({publicComments.length})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="internal" className="space-y-2">
                  {internalComments.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">No internal comments.</p>
                  ) : (
                    internalComments.map((c) => (
                      <div key={c.id} className="rounded-lg border p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{c.authorName ?? 'Staff'}</span>
                          <span>{formatDateTime(c.createdAt)}</span>
                          <LockIcon className="size-3" />
                        </div>
                        <p className="mt-1.5 text-sm">{c.body}</p>
                      </div>
                    ))
                  )}
                </TabsContent>
                <TabsContent value="public" className="space-y-2">
                  {publicComments.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">No public comments.</p>
                  ) : (
                    publicComments.map((c) => (
                      <div key={c.id} className="rounded-lg border p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {c.authorUserId ? (
                            <>
                              <span className="font-medium text-foreground">{c.authorDisplayName ?? 'Community reporter'}</span>
                              <Badge variant="secondary" className="text-xs">Reporter</Badge>
                            </>
                          ) : (
                            <span className="font-medium text-foreground">{c.authorName ?? 'Staff'}</span>
                          )}
                          <span>{formatDateTime(c.createdAt)}</span>
                          <EyeIcon className="size-3" />
                        </div>
                        <p className="mt-1.5 text-sm">{c.body}</p>
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>

              {/* Add comment form */}
              <div className="space-y-2 border-t pt-3">
                <Textarea
                  placeholder="Write a comment..."
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  rows={3}
                />
                <div className="flex items-center gap-2">
                  <Select
                    value={commentInternal ? 'internal' : 'public'}
                    onValueChange={(v) => setCommentInternal(v === 'internal')}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!commentBody.trim() || commentMutation.isPending}
                  >
                    <SendIcon className="size-4" />
                    Add Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column: actions + info */}
        <div className="space-y-4">
          {/* Assignment */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.assignments && report.assignments.length > 0 ? (
                <div className="space-y-2">
                  {report.assignments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-sm">
                      <span>{a.staffName ?? 'Unknown'}</span>
                      <Badge variant="outline" className="text-xs capitalize">{a.staffRole}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not assigned.</p>
              )}
              <Select onValueChange={handleAssign}>
                <SelectTrigger>
                  <SelectValue placeholder="Assign to staff..." />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map((s) => (
                    <SelectItem key={s.id} value={s.id ?? ''}>
                      {s.displayName} ({s.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {assignMutation.isPending && (
                <p className="text-xs text-muted-foreground">Assigning...</p>
              )}
            </CardContent>
          </Card>

          {/* Reporter contact */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <UserIcon className="size-4" />
                Reporter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {report.reporterName && (
                <div className="flex items-center gap-2">
                  <UserIcon className="size-4 text-muted-foreground" />
                  <span>{report.reporterName}</span>
                </div>
              )}
              {report.reporterEmail && (
                <div className="flex items-center gap-2">
                  <MailIcon className="size-4 text-muted-foreground" />
                  <span>{report.reporterEmail}</span>
                </div>
              )}
              {report.reporterPhone && (
                <div className="flex items-center gap-2">
                  <PhoneIcon className="size-4 text-muted-foreground" />
                  <span>{report.reporterPhone}</span>
                </div>
              )}
              {report.contactPrivacy && (
                <Badge variant="secondary" className="gap-1">
                  <ShieldIcon className="size-3" />
                  Private
                </Badge>
              )}
              {!report.reporterName && !report.reporterEmail && !report.reporterPhone && (
                <p className="text-muted-foreground">No contact details provided.</p>
              )}
            </CardContent>
          </Card>

          {/* Notifications */}
          {report.notifications && report.notifications.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BellIcon className="size-4" />
                  Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {report.notifications.map((n) => (
                  <div key={n.id} className="rounded-lg border p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{n.subject}</span>
                      <Badge
                        variant={n.status === 'sent' ? 'default' : n.status === 'failed' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {n.status}
                      </Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground">To: {n.recipientEmail}</p>
                    {n.error && <p className="mt-0.5 text-destructive">{n.error}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Transition dialog */}
      <Dialog open={transitionOpen} onOpenChange={setTransitionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Report Status</DialogTitle>
            <DialogDescription>
              Current status: {statusCfg.label}. The note will be visible on the public tracking page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select new status..." />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.filter((o) => allowed.includes(o.value)).map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {newStatus === 'duplicate' && (
              <Input
                placeholder="Canonical report ID (optional)"
                value={canonicalReportId}
                onChange={(e) => setCanonicalReportId(e.target.value)}
              />
            )}
            <Textarea
              placeholder="Public note (required)..."
              value={transitionNote}
              onChange={(e) => setTransitionNote(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransitionOpen(false)}>Cancel</Button>
            <Button
              onClick={handleTransition}
              disabled={!newStatus || !transitionNote.trim() || transitionMutation.isPending}
            >
              {transitionMutation.isPending ? 'Transitioning...' : 'Confirm Transition'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge duplicate dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge as Duplicate</DialogTitle>
            <DialogDescription>
              Mark this report as a duplicate and transfer its upvotes to the canonical report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="Canonical report ID..."
              value={mergeCanonicalId}
              onChange={(e) => setMergeCanonicalId(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleMerge}
              disabled={!mergeCanonicalId.trim() || mergeMutation.isPending}
            >
              {mergeMutation.isPending ? 'Merging...' : 'Merge Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
